import { useFetcher } from "react-router"
import { useCallback } from "react"

import type { EnhancedConfigData } from "./brand-lenses-configuration"
import { SharedRetentionTab, type RetentionConfig, type RetentionFetcherResult } from "../shared/SharedRetentionTab"

export function RetentionTab({ data, config }: { data: EnhancedConfigData; config: RetentionConfig }) {
  const fetcher = useFetcher<RetentionFetcherResult>()
  const pendingAction = (fetcher.json as { action?: string } | undefined)?.action ?? null

  const handleSave = useCallback(
    (newConfig: RetentionConfig) => {
      const quickExtensionOptions = (newConfig.quickExtensionOptions ?? []).map((option) => {
        const duration: { months?: number; days?: number; hours?: number } = {}
        if (option.duration.months !== undefined) duration.months = option.duration.months
        if (option.duration.days !== undefined) duration.days = option.duration.days
        if (option.duration.hours !== undefined) duration.hours = option.duration.hours
        return { label: option.label, duration }
      })

      fetcher.submit(
        {
          action: "SAVE_SECTION",
          organisation: data.loadedOrganisation,
          project: data.loadedProject,
          jsonPath: "$.retention",
          value: {
            showRetentionSection: newConfig.showRetentionSection ?? false,
            quickExtensionOptions,
            enableDateCalendar: newConfig.enableDateCalendar ?? false,
            enableMDHEntry: newConfig.enableMDHEntry ?? false,
            caseSearchFilters: {
              allowDeletedCases: newConfig.caseSearchFilters?.allowDeletedCases ?? false,
              allowLegalHold: newConfig.caseSearchFilters?.allowLegalHold ?? false,
              allowExpiredCases: newConfig.caseSearchFilters?.allowExpiredCases ?? false,
              allowDeletionRequested: newConfig.caseSearchFilters?.allowDeletionRequested ?? false,
              allowIndefinitelyRetained: newConfig.caseSearchFilters?.allowIndefinitelyRetained ?? false,
              allowExpiresBefore: newConfig.caseSearchFilters?.allowExpiresBefore ?? false,
            },
          },
          concurrency: data.existingConfig?.concurrency ?? "",
        },
        { method: "post", encType: "application/json" },
      )
    },
    [fetcher, data],
  )

  const handleRemove = useCallback(() => {
    const submitData = {
      action: "REMOVE_SECTION",
      organisation: data.loadedOrganisation,
      project: data.loadedProject,
      jsonPath: "$.retention",
      concurrency: data.existingConfig?.concurrency ?? "",
    }

    fetcher.submit(submitData, {
      method: "post",
      encType: "application/json",
    })
  }, [fetcher, data])

  return (
    <SharedRetentionTab
      config={config}
      onSave={handleSave}
      onRemove={handleRemove}
      isFetching={fetcher.state !== "idle"}
      currentAction={pendingAction as "SAVE_SECTION" | "REMOVE_SECTION" | null}
      fetcherData={fetcher.data}
    />
  )
}
